/**
 * Button Adapter for the design system
 * 
 * This component adapts button styling to fit our premium design system.
 * It follows the million-dollar UI aesthetic with gradients, animations,
 * and subtle shadow effects as seen in the hero component.
 */
import * as React from "react"
import { ButtonBaseProps } from "@repo/design-system/types"
import { buttonVariants as shadcnButtonVariants } from "../button"
import { cn } from "@/lib/utils"

// Update interface to extend local types
export interface ButtonProps 
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    ButtonBaseProps {}

/**
 * Premium Button adapter for the design system
 * 
 * Available variants:
 * - default: Primary styling with gradient background
 * - primary: Alias for default
 * - secondary: Light/dark background with colored text
 * - outline: Outlined button with hover effects
 * - ghost: Transparent button with hover effects
 * - link: Gradient text with underline effect
 * - destructive: Red gradient with destructive styling
 * 
 * Available sizes:
 * - sm: Small size
 * - md: Medium size (default)
 * - lg: Large size
 * 
 * @example
 * ```tsx
 * <Button 
 *   variant="primary"
 *   size="md"
 * >
 *   Click me
 * </Button>
 * ```
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ 
    className,
    variant = "default",
    size = "md",
    disabled,
    loading,
    fullWidth,
    startIcon,
    endIcon,
    children,
    onPress,
    onClick,
    type = "button",
    asChild = false,
    radius,
    ...props 
  }, ref) => {
    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(e);
      onPress?.();
    };

    // Maps our variant names to CSS classes using premium tokens
    const getVariantClass = (variant: string) => {
      switch (variant) {
        case 'primary':
        case 'default':
          return "text-white premium-gradient-primary";
        case 'secondary':
          return "text-primary-600 dark:text-primary-400 bg-white dark:bg-gray-800 border border-primary-200 dark:border-primary-800/30";
        case 'outline':
          return "bg-transparent border border-primary-200 dark:border-primary-800/30 hover:border-primary-300 dark:hover:border-primary-700 text-primary-600 dark:text-primary-400";
        case 'ghost':
          return "bg-transparent hover:bg-primary-50 dark:hover:bg-primary-900/10 text-primary-600 dark:text-primary-400";
        case 'link':
          return "premium-gradient-text underline-offset-4 hover:underline";
        case 'destructive':
          return "text-white bg-gradient-to-r from-red-500 to-red-600 dark:from-red-600 dark:to-red-700";
        default:
          return "text-white premium-gradient-primary";
      }
    };

    const variantClass = getVariantClass(variant);
    const sizeClass = shadcnButtonVariants.size[size as keyof typeof shadcnButtonVariants.size] || shadcnButtonVariants.size.md;
    const fullWidthClass = fullWidth ? "w-full" : "";

    // Use premium classes with strong styling
    const roundedClass = radius 
      ? `premium-radius-${radius}` 
      : "premium-radius-button";
      
    // Apply premium hover effects
    const hoverEffectClass = "premium-hover-lift"; 
    
    // Apply premium shadow based on variant
    const shadowClass = 
      variant === 'ghost' || variant === 'link' 
        ? "" 
        : variant === 'outline' 
          ? "premium-shadow-sm" 
          : "premium-shadow-md";
          
    // Apply premium gradient for primary variant
    const gradientClass = 
      variant === 'primary' || variant === 'default'
        ? "premium-gradient-primary"
        : "";
        
    // Apply premium elevation for 3D effect
    const elevationClass = "premium-button-elevated";
    
    return (
      <button
        className={cn(
          shadcnButtonVariants.base,
          variantClass,
          sizeClass,
          fullWidthClass,
          roundedClass,
          shadowClass,
          loading && "opacity-70 pointer-events-none",
          hoverEffectClass,
          gradientClass,
          elevationClass,
          "transform-gpu will-change-transform transition-all duration-300",
          className
        )}
        ref={ref}
        disabled={disabled || loading}
        onClick={handleClick}
        type={type}
        {...props}
      >
        {loading && (
          <svg
            className="mr-2 h-4 w-4 animate-spin text-current transform-gpu"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
        )}
        {startIcon && <span className="mr-2 text-current">{startIcon}</span>}
        {children}
        {endIcon && <span className="ml-2 text-current">{endIcon}</span>}
      </button>
    )
  }
)
Button.displayName = "Button"

export { Button }
